import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { PrimeQueryKeys } from "../shared/constants";

import { Box, Stack, Typography } from "@mui/joy";
import "@fontsource/overpass/900.css";

import { Panel, PanelGroup } from "react-resizable-panels";

import { ConversationApi } from "../api/conversation";
import useCreateConversationAndNavigate from "../hooks/useCreateConversationAndNavigate";
import useSendMessageMutation from "../hooks/useSendMessageMutation";
import { getMessageForSpreadsheetArgs } from "../util/objectUtil";

import Messages from "./Messages";
import MessageInput from "./MessageInput";
import FileViewer, { FileViewerArgs } from "./fileviewer/FileViewer";
import { FileUploadProps } from "./FileUpload";
import Sidebar from "./Sidebar";
import ConversationList from "./ConversationList";
import { ApiFragmentNode, ApiMessageTypes } from "../shared/interfaces";
import FloatingRelatedItems from "./nodes/FloatingRelatedItems";
import { StyledPanelResizeHandle } from "./StyledPanelResizeHandle";
import { setPageTitle } from "../util/elementUtil";

const styles = {
    conversationTitle: {
        p: 2,
        spacing: 1,
        flexShrink: 0,
    },
    conversationTitleHeadingText: {
        fontFamily: "Overpass",
        display: "run-in",
    },
    messagesContainer: {
        flexGrow: 1,
        overflowY: "auto",
    },
    messageInput: {
        px: 1,
        pb: 2,
        maxWidth: "900px",
        width: "100%",
        alignSelf: "center",
    },
    mainColumn: { height: "100%" },
    topContainer: {
        flex: "1 1 auto",
        position: "relative",
        overflowY: "auto",
        display: "flex",
    },
    rightColumn: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        paddingTop: 2,
        minWidth: "600px",
        height: "100%",
    },
    rightColumnContent: { position: "relative", height: "100%" },
    rightColumnConversationItem: { p: 1 },
    conversationsHeading: { paddingBottom: 1 },
};

const FileViewerMemo = React.memo(FileViewer);

export default function Conversation() {
    const [currentFileViewerFile, setCurrentFileViewerFile] = useState<FileViewerArgs | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<FileUploadProps[]>([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(currentFileViewerFile !== null);

    const [selectedFragmentNode, setSelectedFragmentNode] = useState<ApiFragmentNode | null>(null);

    const { id } = useParams();
    const conversationId = id!;

    useEffect(() => {
        setSidebarCollapsed(currentFileViewerFile !== null);
    }, [currentFileViewerFile]);

    useEffect(() => {
        setCurrentFileViewerFile(null);
    }, [conversationId]);

    const query = useQuery(PrimeQueryKeys.conversation(conversationId), () =>
        ConversationApi.getConversation(conversationId),
    );

    useEffect(() => {
        setPageTitle(query.data?.title);
    }, [query.data]);

    const createConversation = useCreateConversationAndNavigate();

    const messageMutation = useSendMessageMutation();
    const submitMessage = (value: string, node?: ApiFragmentNode | undefined) => {
        messageMutation.mutate({
            conversationId: conversationId,
            message: value,
            messageType: ApiMessageTypes.Markdown,
            fragmentNode: node,
            files: uploadedFiles,
        });
    };

    // Check if waiting for a reply to this conversation
    const queryClient = useQueryClient();
    const waitingForReply = queryClient
        .getMutationCache()
        .getAll()
        .some((m) => {
            const mutationConversationId = (m.options.variables as any).conversationId;
            return m.state.status === "loading" && mutationConversationId === conversationId;
        });

    const conversation = query.data;
    const messages = query.data?.messages ?? [];

    return (
        <Stack direction="row" sx={styles.topContainer}>
            {selectedFragmentNode && (
                <FloatingRelatedItems
                    fragmentNode={selectedFragmentNode}
                    onClose={() => setSelectedFragmentNode(null)}
                    setFragmentNode={(selectedFragmentNode) =>
                        setSelectedFragmentNode(selectedFragmentNode)
                    }
                    onMessageSent={submitMessage}
                />
            )}

            <Sidebar headerContent="Conversations" collapsed={sidebarCollapsed}>
                <ConversationList />
            </Sidebar>

            <PanelGroup direction="horizontal" autoSaveId="conversation">
                <Panel minSize={30} id="conversation" order={1}>
                    <Stack direction="column" sx={styles.mainColumn}>
                        <Stack spacing={1} sx={styles.conversationTitle}>
                            <Typography level="h4" sx={styles.conversationTitleHeadingText}>
                                {conversation ? conversation.title : "Loading.."}
                            </Typography>
                        </Stack>

                        <Stack sx={styles.messagesContainer}>
                            <Messages
                                messages={messages}
                                loadingFromServer={query.isLoading && !query.isFetched}
                                waitingForReply={waitingForReply}
                                setCurrentFileViewerFile={setCurrentFileViewerFile}
                                currentFileViewerFile={currentFileViewerFile}
                            />

                            <Box sx={styles.messageInput}>
                                <MessageInput
                                    uploadedFiles={uploadedFiles}
                                    setUploadedFiles={setUploadedFiles}
                                    onSubmit={submitMessage}
                                    onSubmitGenerateSpreadsheet={(args) => {
                                        createConversation(
                                            getMessageForSpreadsheetArgs(args),
                                            ApiMessageTypes.GenerateSpreadsheet,
                                        );
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                </Panel>

                {currentFileViewerFile && (
                    <>
                        <StyledPanelResizeHandle />

                        <Panel minSize={40} id="file" order={2}>
                            <Stack spacing={0} sx={styles.rightColumn}>
                                <FileViewerMemo
                                    fileArgs={currentFileViewerFile}
                                    onClose={() => setCurrentFileViewerFile(null)}
                                    onFragmentNodeSelected={(node) => {
                                        setSelectedFragmentNode(node);
                                    }}
                                    showTitleAndClose={true}
                                />
                            </Stack>
                        </Panel>
                    </>
                )}
            </PanelGroup>
        </Stack>
    );
}
